import { injectable } from "inversify";
import { useUserManagementStore } from "@presentation/stores";
import { jwtDecode } from "jwt-decode";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { UserSession } from "@domain/entities/UserSession";
import { IAuthService } from "@application/contracts/services";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { UserCredentials } from "@domain/schemas/BI";
import axios from "axios";

/**
 * IMPORTANT: Currently the API returns 400 if we attempt to refresh before expiry.
 * Keep buffer at 0 to avoid premature refresh attempts.
 */
const TOKEN_EXPIRY_BUFFER = 0;

/**
 * Minimal in-memory mutex for single-tab serialization (no dependencies).
 * If we need cross-tab serialization, we could add Web Locks / localStorage lock.
 */
class SimpleMutex {
  private locked = false;
  private waiters: Array<() => void> = [];

  async lock(): Promise<() => void> {
    return new Promise<() => void>((resolve) => {
      const unlock = () => {
        const next = this.waiters.shift();
        if (next) next();
        else this.locked = false;
      };

      if (this.locked) {
        this.waiters.push(() => {
          this.locked = true;
          resolve(unlock);
        });
      } else {
        this.locked = true;
        resolve(unlock);
      }
    });
  }
}

/**
 * AuthService handles authentication-related operations such as
 * CSRF token retrieval, access token refresh, token expiration checks,
 * sign-in, and sign-out. It integrates with Zustand for session state management.
 */
@injectable()
export class AuthService implements IAuthService {
  /** Single-flight for refresh: only one network refresh at a time. */
  private static refreshMutex = new SimpleMutex();
  private static refreshPromise: Promise<string> | null = null;

  /** Deduplicate concurrent CSRF token fetches. */
  private static inflightCsrf: Promise<string> | null = null;

  /**
   * Signs the user in by exchanging the OIDC cookie for an access token.
   * Uses the CSRF token from the store, and only fetches a new one if needed.
   * @throws Error if CSRF token or access token exchange fails.
   */
  public async signIn(): Promise<void> {
    const response = await this.tryWithCsrfRetry("GET", API_ENDPOINTS.token);

    if (!response.ok) {
      throw new Error("Failed to exchange OIDC cookie for access token.");
    }

    const raw: unknown = await response.json();
    if (typeof raw !== "string") {
      throw new Error("Invalid access token payload.");
    }

    this.updateSessionWithToken(raw);
  }

  /**
   * Signs the user in using credentials (username and password).
   * Returns the access token directly in the response body and sets refresh token as HttpOnly cookie.
   * @param userCredentials - The user's credentials.
   * @throws Error if the sign-in fails.
   */
  public async signInWithCredentials(userCredentials: UserCredentials): Promise<void> {
    sessionStorage.setItem("signInProgress", "true");
    try {
      const response = await axios.put(`${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.credentials}?origin=${encodeURIComponent(window.location.origin)}`, userCredentials, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      const raw: unknown = response.data;
      if (typeof raw !== "string") {
        throw new Error("Invalid access token payload.");
      }

      this.updateSessionWithToken(raw);
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.error("Failed to login:", err);
      throw err;
    }
  }

  /**
   * Returns true if the token is expired or invalid (with buffer).
   */
  public isTokenExpired(token: string | null): boolean {
    try {
      if (!token) return true;
      const decoded = jwtDecode<{ exp: number }>(token);
      return Date.now() >= decoded.exp * 1000 - TOKEN_EXPIRY_BUFFER;
    } catch {
      return true;
    }
  }

  /**
   * Refreshes the access token using a CSRF-protected endpoint.
   * Single-flight: only one network refresh happens; concurrent callers share it.
   * @returns The new access token as a string.
   * @throws Error if the refresh fails.
   */
  public async refreshAccessToken(): Promise<string> {
    // If a refresh is already in-flight, await it.
    if (AuthService.refreshPromise) {
      return AuthService.refreshPromise;
    }

    // Serialize creation of the in-flight promise to avoid races setting refreshPromise.
    const unlock = await AuthService.refreshMutex.lock();
    try {
      if (!AuthService.refreshPromise) {
        AuthService.refreshPromise = (async () => {
          return await this.refreshOnce();
        })().finally(() => {
          AuthService.refreshPromise = null;
        });
      }
      return await AuthService.refreshPromise;
    } finally {
      unlock();
    }
  }

  /**
   * Performs the actual refresh network call and updates session state once.
   * Assumes caller ensured single-flight semantics.
   */
  private async refreshOnce(): Promise<string> {
    const response = await this.tryWithCsrfRetry("PUT", API_ENDPOINTS.token);

    if (!response.ok) {
      // Do not clear session here; avoid logging user out due to concurrency or policy (e.g., "not expired" => 400).
      throw new Error("Failed to refresh access token.");
    }

    const raw: unknown = await response.json();
    if (typeof raw !== "string") {
      throw new Error("Invalid access token payload.");
    }
    const accessToken = raw;

    // Update session atomically
    const currentState = useUserManagementStore.getState();
    const username = this.getUsernameFromJwt(accessToken);
    const xsrfToken = currentState.userSession?.xsrfToken ?? null;

    useUserManagementStore.setState({
      userSession: new UserSession(username, accessToken, xsrfToken),
    });

    return accessToken;
  }

  /**
   * Signs the user out by calling the backend and clearing local session state.
   * Requires the refresh token cookie.
   * @returns The response payload from the sign-out request.
   * @throws Error if the sign-out fails.
   */
  public async signOut(): Promise<string> {
    // Clear local state optimistically
    useUserManagementStore.setState({
      userSession: new UserSession(null, null, null),
    });

    const response = await this.tryWithCsrfRetry("DELETE", API_ENDPOINTS.token);

    if (!response.ok) {
      // Keep user as signed-out locally even if backend sign-out failed;
      throw new Error("Failed to sign out.");
    }

    localStorage.removeItem("userSession");
    // Also clear default Authorization header if we will ever use axios.defaults.
    // delete axios.defaults.headers.common["Authorization"];

    const payloadRaw: unknown = await response.json();
    if (typeof payloadRaw !== "string") {
      // Some backends might return empty body on sign-out; adapt if needed.
      return "";
    }
    return payloadRaw;
  }

  /**
   * Attempts a fetch request using the CSRF token from the store.
   * If the request fails with 400 (likely due to CSRF mismatch), it retries once
   * with a freshly fetched CSRF token (deduplicated across concurrent callers).
   */
  private async tryWithCsrfRetry(method: "GET" | "PUT" | "DELETE", endpoint: string): Promise<Response> {
    const currentState = useUserManagementStore.getState();
    let xsrfToken = currentState.userSession?.xsrfToken ?? null;

    const makeRequest = (): Promise<Response> =>
      fetch(`${runtimeConfig.API_BASE_URL}${endpoint}`, {
        method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(xsrfToken ? { "X-XSRF-TOKEN": xsrfToken } : {}),
        },
      });

    let response: Response | null = null;

    if (xsrfToken) {
      response = await makeRequest();
    }

    // If no token or CSRF rejected (400), fetch CSRF token once and retry.
    if (!response || response.status === 400) {
      xsrfToken = await this.fetchAndStoreCsrfToken();
      response = await makeRequest();
    }

    return response;
  }

  /**
   * Fetches a new CSRF token from the backend and updates the store.
   * Deduplicates concurrent fetches so only one network call is performed.
   * @returns The new CSRF token.
   */
  private async fetchAndStoreCsrfToken(): Promise<string> {
    if (!AuthService.inflightCsrf) {
      AuthService.inflightCsrf = (async () => {
        const response = await fetch(`${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.csfrToken}`, {
          method: "GET",
          credentials: "include",
        });

        if (!response.ok) throw new Error("Failed to get CSRF token.");

        const xsrfRaw: unknown = await response.json();
        if (typeof xsrfRaw !== "string") {
          throw new Error("CSRF token not found or invalid.");
        }
        const xsrfToken = xsrfRaw;

        const currentState = useUserManagementStore.getState();
        useUserManagementStore.setState({
          userSession: new UserSession(currentState.userSession?.username ?? null, currentState.userSession?.accessToken ?? null, xsrfToken),
        });

        return xsrfToken;
      })().finally(() => {
        AuthService.inflightCsrf = null;
      });
    }

    return AuthService.inflightCsrf;
  }

  /**
   * Updates the user session with a new access token.
   * Extracts the username from the JWT and preserves the existing CSRF token.
   * @param accessToken - The new access token.
   */
  private updateSessionWithToken(accessToken: string): void {
    const username = this.getUsernameFromJwt(accessToken);
    const xsrfToken = useUserManagementStore.getState().userSession?.xsrfToken ?? null;

    useUserManagementStore.setState({
      userSession: new UserSession(username, accessToken, xsrfToken),
    });
  }

  /**
   * Extracts the username from a JWT access token using jwtDecode.
   */
  private getUsernameFromJwt(token: string): string | null {
    try {
      const payload = jwtDecode<{ name?: string }>(token);
      return typeof payload.name === "string" ? payload.name : null;
    } catch {
      return null;
    }
  }
}
